/**
 * The head's identity marks: the position badge, the cover still, the name over its muscle line. One
 * drawing for the day form's card and for a change to an exercise under review (board U), so the
 * two cannot drift apart.
 *
 * `reserve` keeps the still's box and the muscle line's height standing while the catalogue that
 * fills them has not answered, and where it never will: a card that appears inside a conversation
 * must not grow when a read it did not ask for lands. The day form passes `false` and draws exactly
 * what it always drew.
 */

import { muscleColour } from '~/shared/ui/muscleColour';

import type { ExerciseView } from '../model/dayProgramming';

import styles from './ExerciseBlock.module.css';

interface ExerciseIdentityProps {
  readonly exercise: ExerciseView;
  readonly headingId: string;
  /** Inside a superset frame: the marker takes the frame's ink. */
  readonly isGrouped: boolean;
  readonly reserve: boolean;
}

export function ExerciseIdentity({ exercise, headingId, isGrouped, reserve }: ExerciseIdentityProps) {
  return (
    <>
      {exercise.ordinal !== null && (
        <span className={`${String(styles.marker)} ${isGrouped ? String(styles.bracketMarker) : ''}`} aria-hidden="true">
          {exercise.ordinal}
        </span>
      )}

      {exercise.thumbnailUrl !== null ? (
        // Decorative: the movement's name is right beside it, so alternative text would make a
        // screen reader read the name twice.
        <img className={styles.thumbnail} src={exercise.thumbnailUrl} alt="" loading="lazy" />
      ) : (
        reserve && <span className={styles.thumbnail} aria-hidden="true" />
      )}

      <span className={styles.identity}>
        <h4 id={headingId} className={styles.name} title={exercise.name}>
          {exercise.name}
        </h4>
        {/* The canon's muscle line under the name — a coloured pip and one word. The pip is
          * `aria-hidden` because the word beside it says the same thing; the colour is a second
          * channel on top of the label, never the label itself. */}
        {exercise.muscle !== null ? (
          <span className={styles.muscleLine}>
            <i className={styles.musclePip} style={{ background: muscleColour(exercise.muscle.group) }} aria-hidden="true" />
            {exercise.muscle.label}
          </span>
        ) : (
          reserve && <span className={`${String(styles.muscleLine)} ${String(styles.muscleReserve)}`} aria-hidden="true" />
        )}
      </span>
    </>
  );
}
